import React, { useMemo } from 'react';
import { DownloadIcon, MailIcon } from 'lucide-react';
import { termsMeta, termsSections } from '../data/termsOfService';
import { PolicyContents } from '../components/legal/PolicyContents';
import { PolicySectionBody } from '../components/legal/PolicySectionBody';
import { BackToTop } from '../components/legal/BackToTop';
import { SiteFooter } from '../components/ui/SiteFooter';
import { useActiveSection } from '../hooks/useActiveSection';
const metaLinkStyles = 'inline-flex items-center gap-2 text-sm font-medium text-purple-700 underline underline-offset-4 decoration-purple-300 hover:text-purple-900 hover:decoration-purple-700 transition-colors duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 focus-visible:ring-offset-2 focus-visible:ring-offset-ivory rounded';
export function TermsOfServicePage() {
  const sectionIds = useMemo(() => termsSections.map(section => section.id), []);
  const activeId = useActiveSection(sectionIds);
  return <div className="min-h-screen bg-ivory font-sans text-charcoal selection:bg-purple-200 selection:text-charcoal" data-id="element-787" data-mp="7uyybntqj9qg">
      {/* Navigation */}
      <nav className="sticky top-0 left-0 right-0 z-50 px-6 py-6 flex justify-between items-center max-w-7xl mx-auto bg-ivory/95 backdrop-blur-sm border-b border-charcoal/5" data-id="element-788" data-mp="cotdqf2b52jq">
        <a href="/" aria-label="Fem Entity home" data-id="element-789" data-mp="2xgmnpoc2ger">
          <img src="https://cdn.magicpatterns.com/uploads/xpYure8muF9bisffxXgVg2/fementity.png" alt="Fem Entity" className="h-8 md:h-10" data-id="element-790" data-mp="qmrkozeatwp7" />
        </a>
        <div className="hidden md:flex space-x-8 text-sm font-medium text-charcoal/80" data-id="element-791" data-mp="hm3danjeuula">
          <a href="/" className="hover:text-purple-700 transition-colors" data-id="element-792" data-mp="mkwfvkx5t16o">
            Home
          </a>
          <a href="/get-the-app" className="hover:text-purple-700 transition-colors" data-id="element-793" data-mp="32cc0bpjypqet">
            Download the App
          </a>
          <a href="/practitioners" className="hover:text-purple-700 transition-colors" data-id="element-794" data-mp="37c6pnoo3a7xr">
            Practitioners
          </a>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-795" data-mp="1cwridqp6uccm">
        {/* Title area */}
        <header className="pt-16 md:pt-24 pb-10 md:pb-14 border-b border-charcoal/10" data-id="element-796" data-mp="17xx1wvep0zvo">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-purple-700 mb-5" data-id="element-797" data-mp="133apqq0sf3aj">
            {termsMeta.company}
          </p>
          <h1 className="font-serif text-4xl md:text-6xl text-charcoal leading-[1.05] tracking-tight" data-id="element-798" data-mp="u45ovd1r6xv7">
            {termsMeta.title}
          </h1>
          <p className="mt-6 max-w-[62ch] text-lg text-charcoal/70 font-light leading-relaxed" data-id="element-799" data-mp="p3rkq639w82g">
            {termsMeta.summary}
          </p>

          <dl className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm" data-id="element-800" data-mp="g4xoigtutjpf">
            <div className="flex items-baseline gap-2" data-id="element-801" data-mp="g70mbn190h4p">
              <dt className="text-charcoal/50" data-id="element-802" data-mp="2eizf1o1ikeei">Effective</dt>
              <dd className="font-medium text-charcoal" data-id="element-803" data-mp="2aiyq39gsgkt7">{termsMeta.effectiveDate}</dd>
            </div>
          </dl>

          <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4" data-id="element-804" data-mp="3sh3nky44otb2">
            <a href={termsMeta.pdfUrl} target="_blank" rel="noopener noreferrer" className={metaLinkStyles} data-id="element-805" data-mp="19r6sk75qhvo">
              <DownloadIcon aria-hidden="true" className="w-4 h-4" data-id="element-806" data-mp="607ls8qmmbk5" />
              Download the PDF
            </a>
            <a href={`mailto:${termsMeta.email}`} className={metaLinkStyles} data-id="element-807" data-mp="a19jgl4wafk0">
              <MailIcon aria-hidden="true" className="w-4 h-4" data-id="element-808" data-mp="39hzh9x3z1dn3" />
              {termsMeta.email}
            </a>
          </div>
        </header>

        {/* Index + body */}
        <div className="lg:grid lg:grid-cols-[16rem_minmax(0,1fr)] lg:gap-14 xl:gap-20 pb-24 md:pb-32" data-id="element-809" data-mp="3eidppg9hipln">
          <PolicyContents sections={termsSections} activeId={activeId} label="Terms of Service sections" data-id="element-810" data-mp="3nh7iswnlwxug" />

          <article className="max-w-[68ch] pt-10 lg:pt-14" data-id="element-811" data-mp="xs5ju6nf2j2v">
            <p className="pb-12 text-base text-charcoal/80 leading-[1.8]" data-id="element-812" data-mp="xrngyr6916lz">{termsMeta.preamble}</p>

            <div className="divide-y divide-charcoal/10 border-t border-charcoal/10" data-id="element-813" data-mp="12p5g5pugs7gs">
              {termsSections.map(section => <PolicySectionBody key={section.id} section={section} data-id="element-814" data-mp="waztvwm4j25f" />)}
            </div>

            <p className="pt-12 text-sm text-charcoal/50" data-id="element-815" data-mp="1aa984qqrrm6r">
              © 2026 Fem Entity Inc. All rights reserved. Effective {termsMeta.effectiveDate}.
            </p>
          </article>
        </div>
      </main>

      <BackToTop data-id="element-816" data-mp="1k7x57nbul5s3" />

      <SiteFooter currentPath="/terms-of-service" data-id="element-817" data-mp="1k7dxezfwvxrs" />
    </div>;
}